import type {
  HyperliquidAssetIdentity,
  HyperliquidOutcomeAssetIdentity,
  HyperliquidPerpAssetIdentity,
  HyperliquidSpotAssetIdentity,
  HyperliquidSpotTokenMetadata,
  MarketIdentity,
  PortFailure,
  ProviderAssetIdentity,
  Result,
  SpotTokenIdentity,
  SupportIdentifier,
} from '@infinex/types';

/**
 * Hyperliquid asset identity is owned here rather than by common callers.
 *
 * Current perps resolve against explicit perp metadata sources. Spot metadata
 * and HIP-4 outcome encodings are modeled as separate source types so future
 * spot/outcome support has a home in this wrapper without treating
 * `meta.universe` as the only asset catalog.
 */
export type HyperliquidProtocolAssetTranslation =
  | {
      providerId: 'hyperliquid';
      productKind: 'perp';
      symbol: string;
      providerMarketId: string;
      assetIndex?: number;
      universe?: string;
      dex?: string;
      localUniverseIndex?: number;
    }
  | {
      providerId: 'hyperliquid';
      productKind: 'spot';
      symbol: string;
      providerMarketId: string;
      spotAssetId?: string;
      tokenId?: string;
      quoteToken?: string;
      quoteScope?: 'USDC' | 'non-USDC';
      spotPairIndex?: number;
      signedActionAssetId?: number;
      protocolCoin?: string;
      isCanonicalPair?: boolean;
      baseToken?: HyperliquidSpotTokenMetadata;
      quoteTokenMetadata?: HyperliquidSpotTokenMetadata;
      displayLabel?: string;
      searchKeys?: readonly string[];
    }
  | {
      providerId: 'hyperliquid';
      productKind: 'outcome';
      symbol: string;
      providerMarketId: string;
      hip4AssetId?: `#${string}`;
      tokenId?: `+${string}`;
      encoding?: number;
      actionAssetId?: number;
      collateralToken?: 'USDC';
    };

export type HyperliquidPerpUniverseAsset = {
  name: string;
  szDecimals?: number;
};

export type HyperliquidPerpUniverseSource = {
  productKind: 'perp';
  universe: string;
  dex?: string;
  assets: readonly HyperliquidPerpUniverseAsset[];
  assetOffset?: number;
};

export type HyperliquidSpotMetadataSource = {
  productKind: 'spot';
  assetsBySpotAssetId: Readonly<
    Record<
      string,
      {
        spotAssetId: string;
        tokenId?: string;
        providerMarketId?: string;
      }
    >
  >;
};

export type HyperliquidOutcomeMetadataSource = {
  productKind: 'outcome';
  marketsByHip4AssetId: Readonly<
    Record<
      `#${string}`,
      {
        hip4AssetId: `#${string}`;
        outcomeSides?: readonly string[];
        providerMarketId?: string;
      }
    >
  >;
};

export type HyperliquidAssetMetadataSource =
  | HyperliquidPerpUniverseSource
  | HyperliquidSpotMetadataSource
  | HyperliquidOutcomeMetadataSource;

export type HyperliquidSpotMetaToken = HyperliquidSpotTokenMetadata & {
  deployerTradingFeeShare?: string;
};

export type HyperliquidSpotMetaUniverseEntry = {
  tokens: readonly number[];
  name: string;
  index: number;
  isCanonical?: boolean;
};

export type HyperliquidSpotMetaResponse = {
  tokens: readonly HyperliquidSpotMetaToken[];
  universe: readonly HyperliquidSpotMetaUniverseEntry[];
};

export type HyperliquidSpotCatalogMarket = {
  identity: MarketIdentity;
  protocolCoin: string;
  spotPairIndex: number;
  exchangeAssetId: number;
  signedActionAssetId: number;
  isCanonicalPair: boolean;
  baseToken: HyperliquidSpotTokenMetadata;
  quoteToken: HyperliquidSpotTokenMetadata;
  displayLabel: string;
  searchKeys: readonly string[];
  quoteScope: 'USDC' | 'non-USDC';
  executionState: 'enabled' | 'fixture_gated';
  reason?: 'non_usdc_quote_deferred';
  precision: {
    sizeDecimals: number;
    priceDecimals: number;
  };
};

export type HyperliquidSpotCatalog = {
  markets: readonly HyperliquidSpotCatalogMarket[];
  executableMarkets: readonly HyperliquidSpotCatalogMarket[];
  deferredMarkets: readonly HyperliquidSpotCatalogMarket[];
};

type MaybeHyperliquidAssetIdentity = {
  productKind?: unknown;
  assetIndex?: unknown;
  universe?: unknown;
  dex?: unknown;
  localUniverseIndex?: unknown;
  spotAssetId?: unknown;
  tokenId?: unknown;
  quoteToken?: unknown;
  quoteScope?: unknown;
  spotPairIndex?: unknown;
  actionAssetId?: unknown;
  signedActionAssetId?: unknown;
  protocolCoin?: unknown;
  isCanonicalPair?: unknown;
  baseToken?: unknown;
  quoteTokenMetadata?: unknown;
  displayLabel?: unknown;
  searchKeys?: unknown;
  hip4AssetId?: unknown;
  encoding?: unknown;
  collateralToken?: unknown;
};

const unsupported = (message: string): Result<never, PortFailure> => ({
  ok: false,
  failure: {
    kind: 'unsupported',
    message,
  },
});

const parseFailure = (
  message: string,
  evidence?: Readonly<Record<string, unknown>>
): Result<never, PortFailure> => ({
  ok: false,
  failure: {
    kind: 'parse_error',
    message,
    evidence,
  },
});

export function getHyperliquidPerpAssetOffset(dex?: string): number {
  switch (dex) {
    case undefined:
    case 'main':
      return 0;
    case 'xyz':
      return 110000;
    case 'flx':
      return 120000;
    case 'vntl':
      return 130000;
    default:
      return 0;
  }
}

export function resolveHyperliquidPerpAssetIdentity(params: {
  symbol: string;
  universe: string;
  dex?: string;
  assets: readonly HyperliquidPerpUniverseAsset[];
  assetOffset?: number;
}): Result<HyperliquidPerpAssetIdentity, PortFailure> {
  const localUniverseIndex = params.assets.findIndex(
    (asset) => asset.name === params.symbol
  );

  if (localUniverseIndex === -1) {
    return {
      ok: false,
      failure: {
        kind: 'parse_error',
        message: `Hyperliquid perp asset ${params.symbol} is not present in ${params.universe} metadata.`,
      },
    };
  }

  const assetOffset =
    params.assetOffset ?? getHyperliquidPerpAssetOffset(params.dex);

  return {
    ok: true,
    value: {
      productKind: 'perp',
      universe: params.universe,
      dex: params.dex,
      localUniverseIndex,
      assetIndex: assetOffset + localUniverseIndex,
    },
  };
}

export function deriveHyperliquidSpotSignedActionAssetId(spotInfo: {
  index: number;
}): number {
  return 10000 + spotInfo.index;
}

function toSpotTokenMetadata(
  token: HyperliquidSpotMetaToken | SpotTokenIdentity
): HyperliquidSpotTokenMetadata {
  if ('tokenIndex' in token) {
    return {
      index: token.tokenIndex,
      name: token.symbol,
      fullName: token.name ?? null,
      tokenId: token.tokenId,
      szDecimals: token.decimals ?? 0,
    };
  }

  return {
    index: token.index,
    name: token.name,
    fullName: token.fullName ?? null,
    tokenId: token.tokenId,
    isCanonical: token.isCanonical,
    szDecimals: token.szDecimals,
    weiDecimals: token.weiDecimals,
    evmContract: token.evmContract ?? null,
  };
}

function getSearchKeys(params: {
  protocolCoin: string;
  displayLabel: string;
  baseToken: HyperliquidSpotTokenMetadata;
  quoteToken: HyperliquidSpotTokenMetadata;
}): readonly string[] {
  return [
    params.protocolCoin,
    params.displayLabel,
    ...(params.baseToken.fullName ? [params.baseToken.fullName] : []),
    ...(params.quoteToken.fullName ? [params.quoteToken.fullName] : []),
    params.baseToken.tokenId,
    params.quoteToken.tokenId,
  ];
}

export function getHyperliquidSpotMarketSearchKeys(
  market: Pick<
    HyperliquidSpotCatalogMarket,
    'baseToken' | 'quoteToken' | 'searchKeys'
  >
): readonly string[] {
  return [market.baseToken.name, market.quoteToken.name, ...market.searchKeys];
}

export function matchesHyperliquidSpotMarketSearch(
  market: Pick<
    HyperliquidSpotCatalogMarket,
    'baseToken' | 'quoteToken' | 'searchKeys'
  >,
  searchTerm: string
): boolean {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  if (!normalizedSearchTerm) return true;

  return getHyperliquidSpotMarketSearchKeys(market).some((searchKey) =>
    searchKey.toLowerCase().includes(normalizedSearchTerm)
  );
}

export function buildHyperliquidSpotCatalogFromMeta(
  spotMeta: HyperliquidSpotMetaResponse
): Result<HyperliquidSpotCatalog, PortFailure> {
  const tokensByIndex = new Map<number, HyperliquidSpotTokenMetadata>();
  for (const token of spotMeta.tokens) {
    tokensByIndex.set(token.index, toSpotTokenMetadata(token));
  }

  const markets: HyperliquidSpotCatalogMarket[] = [];

  for (const spotInfo of spotMeta.universe) {
    const [baseTokenIndex, quoteTokenIndex] = spotInfo.tokens;
    if (baseTokenIndex === undefined || quoteTokenIndex === undefined) {
      return parseFailure(
        `Hyperliquid spot market ${spotInfo.name} does not include base and quote token indexes.`,
        { spotInfo }
      );
    }

    const baseToken = tokensByIndex.get(baseTokenIndex);
    const quoteToken = tokensByIndex.get(quoteTokenIndex);
    if (!baseToken || !quoteToken) {
      return parseFailure(
        `Hyperliquid spot market ${spotInfo.name} references missing token metadata.`,
        {
          spotInfo,
          missingBaseTokenIndex: baseToken ? undefined : baseTokenIndex,
          missingQuoteTokenIndex: quoteToken ? undefined : quoteTokenIndex,
        }
      );
    }

    const signedActionAssetId =
      deriveHyperliquidSpotSignedActionAssetId(spotInfo);
    const displayLabel = `${baseToken.name}/${quoteToken.name}`;
    const quoteScope = quoteToken.name === 'USDC' ? 'USDC' : 'non-USDC';
    const searchKeys = getSearchKeys({
      protocolCoin: spotInfo.name,
      displayLabel,
      baseToken,
      quoteToken,
    });
    const assetIdentity = buildHyperliquidSpotAssetIdentity({
      spotAssetId: signedActionAssetId.toString(),
      tokenId: baseToken.tokenId,
      quoteToken: quoteToken.name,
      quoteScope,
      spotPairIndex: spotInfo.index,
      signedActionAssetId,
      protocolCoin: spotInfo.name,
      isCanonicalPair: spotInfo.isCanonical ?? false,
      baseToken,
      quoteTokenMetadata: quoteToken,
      displayLabel,
      searchKeys,
    });

    markets.push({
      identity: {
        providerId: 'hyperliquid',
        productKind: 'spot',
        symbol: displayLabel,
        providerMarketId: `spot:${signedActionAssetId}`,
        assetIdentity,
      },
      protocolCoin: spotInfo.name,
      spotPairIndex: spotInfo.index,
      exchangeAssetId: signedActionAssetId,
      signedActionAssetId,
      isCanonicalPair: spotInfo.isCanonical ?? false,
      baseToken,
      quoteToken,
      displayLabel,
      searchKeys,
      quoteScope,
      executionState: quoteScope === 'USDC' ? 'enabled' : 'fixture_gated',
      reason: quoteScope === 'USDC' ? undefined : 'non_usdc_quote_deferred',
      precision: {
        sizeDecimals: baseToken.szDecimals,
        priceDecimals: Math.max(0, 8 - baseToken.szDecimals),
      },
    });
  }

  return {
    ok: true,
    value: {
      markets,
      executableMarkets: markets.filter(
        (market) => market.quoteScope === 'USDC'
      ),
      deferredMarkets: markets.filter((market) => market.quoteScope !== 'USDC'),
    },
  };
}

export function buildHyperliquidUsdcSpotCatalog(
  spotMeta: HyperliquidSpotMetaResponse
): Result<readonly HyperliquidSpotCatalogMarket[], PortFailure> {
  const catalog = buildHyperliquidSpotCatalogFromMeta(spotMeta);
  if (!catalog.ok) return catalog;

  return {
    ok: true,
    value: catalog.value.executableMarkets,
  };
}

export function translateHyperliquidProtocolAssetIdentity(
  identity: MarketIdentity
): Result<HyperliquidProtocolAssetTranslation, PortFailure> {
  if (identity.providerId !== 'hyperliquid') {
    return unsupported(
      `Cannot translate ${identity.providerId} identity through the Hyperliquid adapter.`
    );
  }

  const assetIdentity = identity.assetIdentity as
    | MaybeHyperliquidAssetIdentity
    | undefined;

  switch (identity.productKind) {
    case 'perp': {
      const perpIdentity =
        assetIdentity && assetIdentity.productKind === 'perp'
          ? assetIdentity
          : undefined;
      return {
        ok: true,
        value: {
          providerId: 'hyperliquid',
          productKind: 'perp',
          symbol: identity.symbol,
          providerMarketId: identity.providerMarketId,
          assetIndex:
            typeof perpIdentity?.assetIndex === 'number'
              ? perpIdentity.assetIndex
              : undefined,
          universe:
            typeof perpIdentity?.universe === 'string'
              ? perpIdentity.universe
              : undefined,
          dex:
            typeof perpIdentity?.dex === 'string'
              ? perpIdentity.dex
              : undefined,
          localUniverseIndex:
            typeof perpIdentity?.localUniverseIndex === 'number'
              ? perpIdentity.localUniverseIndex
              : undefined,
        },
      };
    }

    case 'spot': {
      const spotIdentity =
        assetIdentity && assetIdentity.productKind === 'spot'
          ? assetIdentity
          : undefined;
      const spotRecord = spotIdentity;
      return {
        ok: true,
        value: {
          providerId: 'hyperliquid',
          productKind: 'spot',
          symbol: identity.symbol,
          providerMarketId: identity.providerMarketId,
          spotAssetId:
            typeof spotRecord?.spotAssetId === 'string'
              ? spotRecord.spotAssetId
              : typeof spotRecord?.actionAssetId === 'number'
                ? String(spotRecord.actionAssetId)
                : undefined,
          tokenId:
            typeof spotRecord?.tokenId === 'string'
              ? spotRecord.tokenId
              : typeof spotRecord?.baseToken === 'object' &&
                  spotRecord.baseToken !== null &&
                  typeof (spotRecord.baseToken as { tokenId?: unknown })
                    .tokenId === 'string'
                ? (spotRecord.baseToken as { tokenId: string }).tokenId
                : undefined,
          quoteToken:
            typeof spotRecord?.quoteToken === 'string'
              ? spotRecord.quoteToken
              : typeof spotRecord?.quoteToken === 'object' &&
                  spotRecord.quoteToken !== null &&
                  typeof (spotRecord.quoteToken as { symbol?: unknown })
                    .symbol === 'string'
                ? (spotRecord.quoteToken as { symbol: string }).symbol
                : undefined,
          quoteScope:
            spotRecord?.quoteScope === 'USDC' ||
            spotRecord?.quoteScope === 'non-USDC'
              ? spotRecord.quoteScope
              : typeof spotRecord?.quoteToken === 'object' &&
                  spotRecord.quoteToken !== null &&
                  (spotRecord.quoteToken as { symbol?: unknown }).symbol ===
                    'USDC'
                ? 'USDC'
                : undefined,
          spotPairIndex:
            typeof spotIdentity?.spotPairIndex === 'number'
              ? spotIdentity.spotPairIndex
              : undefined,
          signedActionAssetId:
            typeof spotRecord?.signedActionAssetId === 'number'
              ? spotRecord.signedActionAssetId
              : typeof spotRecord?.actionAssetId === 'number'
                ? spotRecord.actionAssetId
                : undefined,
          protocolCoin:
            typeof spotIdentity?.protocolCoin === 'string'
              ? spotIdentity.protocolCoin
              : undefined,
          isCanonicalPair:
            typeof spotRecord?.isCanonicalPair === 'boolean'
              ? spotRecord.isCanonicalPair
              : undefined,
          baseToken:
            spotRecord?.baseToken && typeof spotRecord.baseToken === 'object'
              ? (spotRecord.baseToken as HyperliquidSpotTokenMetadata)
              : undefined,
          quoteTokenMetadata:
            spotRecord?.quoteTokenMetadata &&
            typeof spotRecord.quoteTokenMetadata === 'object'
              ? (spotRecord.quoteTokenMetadata as HyperliquidSpotTokenMetadata)
              : undefined,
          displayLabel:
            typeof spotRecord?.displayLabel === 'string'
              ? spotRecord.displayLabel
              : undefined,
          searchKeys: Array.isArray(spotRecord?.searchKeys)
            ? spotRecord.searchKeys.filter(
                (key): key is string => typeof key === 'string'
              )
            : undefined,
        },
      };
    }

    case 'outcome': {
      const outcomeIdentity =
        assetIdentity && assetIdentity.productKind === 'outcome'
          ? assetIdentity
          : undefined;
      return {
        ok: true,
        value: {
          providerId: 'hyperliquid',
          productKind: 'outcome',
          symbol: identity.symbol,
          providerMarketId: identity.providerMarketId,
          hip4AssetId:
            typeof outcomeIdentity?.hip4AssetId === 'string' &&
            outcomeIdentity.hip4AssetId.startsWith('#')
              ? (outcomeIdentity.hip4AssetId as `#${string}`)
              : undefined,
          tokenId:
            typeof outcomeIdentity?.tokenId === 'string' &&
            outcomeIdentity.tokenId.startsWith('+')
              ? (outcomeIdentity.tokenId as `+${string}`)
              : undefined,
          encoding:
            typeof outcomeIdentity?.encoding === 'number'
              ? outcomeIdentity.encoding
              : undefined,
          actionAssetId:
            typeof outcomeIdentity?.actionAssetId === 'number'
              ? outcomeIdentity.actionAssetId
              : undefined,
          collateralToken:
            outcomeIdentity?.collateralToken === 'USDC' ? 'USDC' : undefined,
        },
      };
    }
  }
}

export function translateHyperliquidMarketToProviderAssetIdentity(
  identity: MarketIdentity
): ProviderAssetIdentity {
  const translated = translateHyperliquidProtocolAssetIdentity(identity);
  if (!translated.ok) {
    return {
      providerId: identity.providerId,
      productKind: identity.productKind,
      raw: {
        error: translated.failure,
      },
    };
  }

  return {
    providerId: 'hyperliquid',
    productKind: identity.productKind,
    raw: translated.value,
  };
}

export function buildHyperliquidSpotAssetIdentity(params: {
  spotAssetId: string;
  tokenId?: string;
  quoteToken?: string;
  quoteScope?: 'USDC' | 'non-USDC';
  spotPairIndex?: number;
  signedActionAssetId?: number;
  protocolCoin?: string;
  isCanonicalPair?: boolean;
  baseToken?: HyperliquidSpotTokenMetadata;
  quoteTokenMetadata?: HyperliquidSpotTokenMetadata;
  displayLabel?: string;
  searchKeys?: readonly string[];
}): HyperliquidSpotAssetIdentity {
  const baseToken = toSpotTokenIdentity(params.baseToken, {
    tokenId: params.tokenId ?? params.spotAssetId,
    symbol: params.displayLabel?.split('/', 1)[0] ?? params.spotAssetId,
  });
  const quoteToken = toSpotTokenIdentity(params.quoteTokenMetadata, {
    tokenId: 'USDC',
    symbol: 'USDC',
  }) as SpotTokenIdentity & { symbol: 'USDC' };
  const actionAssetId =
    params.signedActionAssetId ?? Number.parseInt(params.spotAssetId, 10);
  const spotPairIndex =
    params.spotPairIndex ?? Math.max(0, actionAssetId - 10_000);

  return {
    productKind: 'spot',
    protocolCoin: toProtocolCoin(spotPairIndex),
    spotPairIndex,
    actionAssetId,
    baseToken,
    quoteToken,
    supportIdentifiers: buildSpotSupportIdentifiers({
      protocolCoin: params.protocolCoin ?? toProtocolCoin(spotPairIndex),
      spotPairIndex,
      actionAssetId,
      baseToken,
      quoteToken,
    }),
  };
}

export function buildHyperliquidSpotFallbackMarketIdentity(
  symbol: string
): MarketIdentity {
  const [baseAsset, quoteAsset] = symbol.includes('/')
    ? symbol.split('/', 2)
    : [undefined, undefined];
  const displayLabel =
    baseAsset && quoteAsset ? `${baseAsset}/${quoteAsset}` : symbol;
  const spotPairIndex = parseHyperliquidProtocolCoin(symbol);

  return {
    providerId: 'hyperliquid',
    productKind: 'spot',
    symbol: displayLabel,
    providerMarketId: symbol,
    ...(spotPairIndex !== undefined
      ? {
          assetIdentity: buildHyperliquidSpotAssetIdentity({
            spotAssetId: String(
              deriveHyperliquidSpotSignedActionAssetId({
                index: spotPairIndex,
              })
            ),
            tokenId: symbol,
            quoteToken: 'USDC',
            quoteScope: 'USDC',
            spotPairIndex,
            protocolCoin: symbol,
            baseToken: {
              index: spotPairIndex,
              name: baseAsset ?? symbol,
              fullName: null,
              tokenId: symbol,
              szDecimals: 8,
            },
            quoteTokenMetadata: HYPERLIQUID_SPOT_USDC_TOKEN,
            displayLabel,
            searchKeys: [symbol, displayLabel, baseAsset ?? symbol, 'USDC'],
          }),
        }
      : {}),
  };
}

function toSpotTokenIdentity(
  token: HyperliquidSpotTokenMetadata | undefined,
  fallback: {
    tokenId: string;
    symbol: string;
  }
): SpotTokenIdentity {
  return {
    tokenIndex: token?.index ?? 0,
    tokenId: token?.tokenId ?? fallback.tokenId,
    symbol: token?.name ?? fallback.symbol,
    name: token?.fullName ?? undefined,
    decimals: token?.szDecimals,
    ...(token?.evmContract !== undefined
      ? { evmContract: token.evmContract }
      : {}),
  };
}

function toProtocolCoin(spotPairIndex: number): `@${number}` {
  return `@${spotPairIndex}`;
}

function parseHyperliquidProtocolCoin(symbol: string): number | undefined {
  if (!symbol.startsWith('@')) return undefined;

  const parsed = Number.parseInt(symbol.slice(1), 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function buildSpotSupportIdentifiers(input: {
  protocolCoin: string;
  spotPairIndex: number;
  actionAssetId: number;
  baseToken: SpotTokenIdentity;
  quoteToken: SpotTokenIdentity;
}): readonly SupportIdentifier[] {
  return [
    {
      kind: 'protocol_coin',
      value: input.protocolCoin,
    },
    {
      kind: 'spot_pair_index',
      value: String(input.spotPairIndex),
    },
    {
      kind: 'action_asset_id',
      value: String(input.actionAssetId),
    },
    {
      kind: 'base_token_index',
      value: String(input.baseToken.tokenIndex),
    },
    {
      kind: 'base_token_id',
      value: input.baseToken.tokenId,
    },
    {
      kind: 'quote_token_index',
      value: String(input.quoteToken.tokenIndex),
    },
    {
      kind: 'quote_token_id',
      value: input.quoteToken.tokenId,
    },
  ];
}

type HyperliquidSpotReferenceMarketParams = {
  protocolCoin: string;
  spotPairIndex: number;
  isCanonicalPair: boolean;
  baseToken: HyperliquidSpotTokenMetadata;
  quoteToken: HyperliquidSpotTokenMetadata;
  displayLabel: string;
  searchKeys: readonly string[];
};

function buildHyperliquidSpotReferenceMarketIdentity(
  params: HyperliquidSpotReferenceMarketParams
): MarketIdentity {
  const signedActionAssetId = deriveHyperliquidSpotSignedActionAssetId({
    index: params.spotPairIndex,
  });
  const quoteScope =
    params.quoteToken.name === 'USDC' ? ('USDC' as const) : 'non-USDC';

  return {
    providerId: 'hyperliquid',
    productKind: 'spot',
    symbol: params.displayLabel,
    providerMarketId: params.protocolCoin,
    assetIdentity: buildHyperliquidSpotAssetIdentity({
      spotAssetId: String(signedActionAssetId),
      tokenId: params.baseToken.tokenId,
      quoteToken: params.quoteToken.name,
      quoteScope,
      spotPairIndex: params.spotPairIndex,
      signedActionAssetId,
      protocolCoin: params.protocolCoin,
      isCanonicalPair: params.isCanonicalPair,
      baseToken: params.baseToken,
      quoteTokenMetadata: params.quoteToken,
      displayLabel: params.displayLabel,
      searchKeys: params.searchKeys,
    }),
  };
}

const HYPERLIQUID_SPOT_USDC_TOKEN: HyperliquidSpotTokenMetadata = {
  index: 0,
  name: 'USDC',
  fullName: null,
  tokenId: '0x6d1e7cde53ba9467b783cb7c530ce054',
  isCanonical: true,
  szDecimals: 8,
  weiDecimals: 8,
  evmContract: {
    address: '0x6b9e773128f453f5c2c60935ee2de2cbc5390a24',
    evm_extra_wei_decimals: -2,
  },
};

const HYPERLIQUID_SPOT_REFERENCE_MARKETS = [
  buildHyperliquidSpotReferenceMarketIdentity({
    protocolCoin: 'PURR/USDC',
    spotPairIndex: 0,
    isCanonicalPair: true,
    baseToken: {
      index: 1,
      name: 'PURR',
      fullName: null,
      tokenId: '0xc1fb593aeffbeb02f85e0308e9956a90',
      isCanonical: true,
      szDecimals: 0,
      weiDecimals: 5,
      evmContract: {
        address: '0x9b498c3c8a0b8cd8ba1d9851d40d186f1872b44e',
        evm_extra_wei_decimals: 13,
      },
    },
    quoteToken: HYPERLIQUID_SPOT_USDC_TOKEN,
    displayLabel: 'PURR/USDC',
    searchKeys: [
      'PURR/USDC',
      'PURR/USDC',
      '0xc1fb593aeffbeb02f85e0308e9956a90',
      '0x6d1e7cde53ba9467b783cb7c530ce054',
    ],
  }),
  buildHyperliquidSpotReferenceMarketIdentity({
    protocolCoin: '@107',
    spotPairIndex: 107,
    isCanonicalPair: false,
    baseToken: {
      index: 150,
      name: 'HYPE',
      fullName: 'Hyperliquid',
      tokenId: '0x0d01dc56dcaaca66ad901c959b4011ec',
      isCanonical: false,
      szDecimals: 2,
      weiDecimals: 8,
      evmContract: null,
    },
    quoteToken: HYPERLIQUID_SPOT_USDC_TOKEN,
    displayLabel: 'HYPE/USDC',
    searchKeys: [
      '@107',
      'HYPE/USDC',
      'Hyperliquid',
      '0x0d01dc56dcaaca66ad901c959b4011ec',
      '0x6d1e7cde53ba9467b783cb7c530ce054',
    ],
  }),
] as const satisfies readonly MarketIdentity[];

function getSpotReferenceIdentity(
  identity: MarketIdentity
): HyperliquidSpotAssetIdentity | undefined {
  const assetIdentity = identity.assetIdentity;
  return isHyperliquidAssetIdentity(assetIdentity) &&
    assetIdentity.productKind === 'spot'
    ? assetIdentity
    : undefined;
}

export function resolveHyperliquidSpotReferenceMarketIdentity(
  symbol: string
): MarketIdentity | undefined {
  return HYPERLIQUID_SPOT_REFERENCE_MARKETS.find((market) => {
    const assetIdentity = getSpotReferenceIdentity(market);

    return (
      market.providerMarketId === symbol ||
      market.symbol === symbol ||
      assetIdentity?.protocolCoin === symbol ||
      String(assetIdentity?.actionAssetId) === symbol
    );
  });
}

export function resolveHyperliquidSpotReferenceMarketForToken(
  tokenIndex: number
): MarketIdentity | undefined {
  return HYPERLIQUID_SPOT_REFERENCE_MARKETS.find((market) => {
    const assetIdentity = getSpotReferenceIdentity(market);

    return (
      assetIdentity?.quoteToken.symbol === 'USDC' &&
      assetIdentity.baseToken.tokenIndex === tokenIndex
    );
  });
}

export function resolveHyperliquidSpotReferenceToken(
  tokenIndex: number
): HyperliquidSpotTokenMetadata | undefined {
  for (const market of HYPERLIQUID_SPOT_REFERENCE_MARKETS) {
    const assetIdentity = getSpotReferenceIdentity(market);
    if (assetIdentity?.baseToken.tokenIndex === tokenIndex) {
      return toSpotTokenMetadata(assetIdentity.baseToken);
    }
    if (assetIdentity?.quoteToken.tokenIndex === tokenIndex) {
      return toSpotTokenMetadata(assetIdentity.quoteToken);
    }
  }

  return undefined;
}

/**
 * HIP-4 order actions address an outcome side as `100_000_000 + encoding`,
 * where `encoding = 10 * outcome + side`.
 */
export const HYPERLIQUID_OUTCOME_ACTION_ASSET_ID_OFFSET = 100_000_000;

export function buildHyperliquidOutcomeAssetIdentity(params: {
  hip4AssetId: `#${string}`;
  tokenId?: `+${string}`;
  encoding?: number;
  actionAssetId?: number;
}): HyperliquidOutcomeAssetIdentity {
  const parsedEncoding = Number(params.hip4AssetId.slice(1));
  const encoding =
    params.encoding ??
    (Number.isInteger(parsedEncoding) && parsedEncoding >= 0
      ? parsedEncoding
      : 0);
  const actionAssetId =
    params.actionAssetId ??
    HYPERLIQUID_OUTCOME_ACTION_ASSET_ID_OFFSET + encoding;
  return {
    productKind: 'outcome',
    hip4AssetId: params.hip4AssetId,
    tokenId: params.tokenId ?? `+${params.hip4AssetId.slice(1)}`,
    encoding,
    actionAssetId,
    collateralToken: 'USDC',
    supportIdentifiers: [
      {
        kind: 'hip4_encoding',
        value: String(encoding),
      },
      {
        kind: 'action_asset_id',
        value: String(actionAssetId),
      },
    ],
  };
}

export function isHyperliquidAssetIdentity(
  identity: MarketIdentity['assetIdentity']
): identity is HyperliquidAssetIdentity {
  return (
    Boolean(identity) &&
    typeof identity === 'object' &&
    'productKind' in identity &&
    (identity.productKind === 'perp' ||
      identity.productKind === 'spot' ||
      identity.productKind === 'outcome')
  );
}
